/**
 * Screenshots for the request form without any library: an image pasted
 * from the clipboard (Ctrl+V), or one frame of the screen the user shares
 * through the browser's own picker (getDisplayMedia), drawn to a canvas and
 * saved as PNG (JPEG, then smaller, if the PNG is over the size limit).
 * Nothing is captured unless the user picks a screen in that picker.
 */
import { FEEDBACK_ATTACHMENT_MAX_BYTES } from '@velora/contracts/feedback';

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/** Minimal shape of DataTransferItemList entries (tests pass plain objects). */
export interface ClipboardItemLike {
  kind: string;
  type: string;
  getAsFile: () => File | null;
}

/** Image files in a paste, renamed `pasted-screenshot-<n>.<ext>` (clipboard names are all "image.png"). */
export function pastedImages(items: ArrayLike<ClipboardItemLike>, startAt = 1): File[] {
  const out: File[] = [];
  for (const item of Array.from(items)) {
    if (item.kind !== 'file' || !IMAGE_TYPES.includes(item.type)) continue;
    const f = item.getAsFile();
    if (!f) continue;
    const ext = item.type.split('/')[1] === 'jpeg' ? 'jpg' : (item.type.split('/')[1] ?? 'png');
    out.push(
      new File([f], `pasted-screenshot-${startAt + out.length}.${ext}`, { type: item.type }),
    );
  }
  return out;
}

/** Whether this browser can share a screen (desktop Chrome, Edge, Firefox, Safari). */
export function canCaptureScreen(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function' &&
    typeof document !== 'undefined'
  );
}

/** Scale so the longer side is at most `max` px (1 when it already fits). */
export function fitScale(width: number, height: number, max: number): number {
  const longer = Math.max(width, height);
  return longer > max ? max / longer : 1;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

async function encode(video: HTMLVideoElement, maxSide: number): Promise<Blob | null> {
  const scale = fitScale(video.videoWidth, video.videoHeight, maxSide);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
  canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  const png = await toBlob(canvas, 'image/png');
  if (png && png.size <= FEEDBACK_ATTACHMENT_MAX_BYTES) return png;
  const jpeg = await toBlob(canvas, 'image/jpeg', 0.85);
  return jpeg && jpeg.size <= FEEDBACK_ATTACHMENT_MAX_BYTES ? jpeg : null;
}

const nextFrame = () => new Promise<void>((r) => requestAnimationFrame(() => r()));
const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Asks the browser to share a screen (the user picks; this tab is offered
 * first where supported), takes one frame and stops sharing at once.
 * Returns null when the user cancels. `beforeFrame` runs once sharing has
 * started (the form hides itself there so it is not in the picture).
 */
export async function captureScreen(beforeFrame?: () => void): Promise<File | null> {
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser' },
      audio: false,
      // Chrome: offer this tab first; ignored elsewhere.
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
    } as DisplayMediaStreamOptions);
  } catch {
    return null;
  }
  const video = document.createElement('video');
  try {
    beforeFrame?.();
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    await video.play();
    // Let the page repaint without the form, and the stream deliver a fresh frame.
    await nextFrame();
    await nextFrame();
    await wait(250);
    let blob = await encode(video, 4096);
    if (!blob) blob = await encode(video, 1920);
    if (!blob) return null;
    const ext = blob.type === 'image/jpeg' ? 'jpg' : 'png';
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    return new File([blob], `screen-${stamp}.${ext}`, { type: blob.type });
  } finally {
    for (const t of stream.getTracks()) t.stop();
    video.srcObject = null;
  }
}
